import { logger } from '../../../utils/logger.js';

export const fetchWeather = async (latitude, longitude) => {
    try {
        // Validate coordinates
        if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
            logger.error('Invalid coordinates provided');
            return null;
        }

        // Fetch weather data
        const weatherUrl = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&temperature_unit=celsius&timezone=auto`;
        logger.info('Fetching weather data');
        
        const weatherResponse = await fetch(weatherUrl);
        logger.info('Weather API response received', { status: weatherResponse.status });
        
        if (!weatherResponse.ok) {
            logger.error('Weather API error', { status: weatherResponse.status });
            throw new Error(`Weather fetch failed: ${weatherResponse.status} ${weatherResponse.statusText}`);
        }
        
        const weatherData = await weatherResponse.json();
        logger.info('Weather data processed');
        
        // Fetch air quality data separately
        try {
            const aqUrl = `https://api.open-meteo.com/v1/air-quality?latitude=${latitude}&longitude=${longitude}&current=us_aqi&timezone=auto`;
            const aqResponse = await fetch(aqUrl);
            if (aqResponse.ok) {
                const aqData = await aqResponse.json();
                weatherData.current.air_quality_index = aqData.current?.us_aqi || null;
                logger.info('Air quality data fetched');
            }
        } catch (aqError) {
            logger.warn('Air quality data unavailable');
        }
        
        return weatherData.current;
    } catch (error) {
        logger.error('Weather fetch failed', error);
        return null;
    }
};

export const getWeatherDescription = (weatherCode) => {
    const weatherCodes = {
        0: 'Clear sky',
        1: 'Mainly clear',
        2: 'Partly cloudy',
        3: 'Overcast',
        45: 'Foggy',
        48: 'Depositing rime fog',
        51: 'Light drizzle',
        53: 'Moderate drizzle',
        55: 'Dense drizzle',
        61: 'Slight rain',
        63: 'Moderate rain',
        65: 'Heavy rain',
        71: 'Slight snow',
        73: 'Moderate snow',
        75: 'Heavy snow',
        77: 'Snow grains',
        80: 'Slight rain showers',
        81: 'Moderate rain showers',
        82: 'Violent rain showers',
        85: 'Slight snow showers',
        86: 'Heavy snow showers',
        95: 'Thunderstorm',
        96: 'Thunderstorm with slight hail',
        99: 'Thunderstorm with heavy hail'
    };
    return weatherCodes[weatherCode] || 'Unknown';
};

export const getWeatherIcon = (weatherCode) => {
    if (weatherCode === 0) return '☀️';
    if (weatherCode === 1 || weatherCode === 2) return '⛅';
    if (weatherCode === 3) return '☁️';
    if (weatherCode === 45 || weatherCode === 48) return '🌫️';
    if (weatherCode >= 51 && weatherCode <= 55) return '🌧️';
    if (weatherCode >= 61 && weatherCode <= 65) return '🌧️';
    if (weatherCode >= 71 && weatherCode <= 77) return '❄️';
    if (weatherCode >= 80 && weatherCode <= 82) return '🌧️';
    if (weatherCode >= 85 && weatherCode <= 86) return '❄️';
    if (weatherCode >= 95 && weatherCode <= 99) return '⛈️';
    return '🌡️';
};

export const getAQIDescription = (aqi) => {
    if (aqi <= 50) return 'Good';
    if (aqi <= 100) return 'Fair';
    if (aqi <= 150) return 'Moderate';
    if (aqi <= 200) return 'Poor';
    if (aqi <= 300) return 'Very Poor';
    return 'Extremely Poor';
};

export const getAQIColor = (aqi) => {
    if (aqi <= 50) return 'text-green-400';
    if (aqi <= 100) return 'text-yellow-400';
    if (aqi <= 150) return 'text-orange-400';
    if (aqi <= 200) return 'text-red-400';
    if (aqi <= 300) return 'text-red-600';
    return 'text-red-800';
};
